import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { motion } from "framer-motion";
import { MapPin, Move3d, Navigation, Clock, Sun, Moon } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "@/i18n";
import { isOpenNow } from "@/lib/hours";

const OFFICE = [46.8987936, 11.4322648];
const GMAPS_ROUTE = "https://www.google.com/maps/dir/?api=1&destination=46.8987936,11.4322648";

const openRoute = () => {
  const isApple = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
  const dest = "46.8987936,11.4322648";
  const url = isApple
    ? `https://maps.apple.com/?daddr=${dest}`
    : GMAPS_ROUTE;
  window.open(url, "_blank", "noopener");
};

const pinIcon = L.divIcon({
  className: "",
  html: '<div style="position:relative;width:14px;height:14px;"><div class="pin"></div></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

function MapController({ active }) {
  const map = useMap();
  const zoomCtrl = useRef(null);

  // Remove the Ukrainian flag from Leaflet's default attribution prefix
  // while keeping a clean "Leaflet" credit.
  useEffect(() => {
    if (map.attributionControl) {
      map.attributionControl.setPrefix(
        '<a href="https://leafletjs.com" target="_blank" rel="noopener noreferrer">Leaflet</a>',
      );
    }
  }, [map]);

  useEffect(() => {
    if (!zoomCtrl.current) zoomCtrl.current = L.control.zoom({ position: "bottomright" });
    if (active) {
      map.dragging.enable();
      map.scrollWheelZoom.enable();
      map.doubleClickZoom.enable();
      map.touchZoom.enable();
      zoomCtrl.current.addTo(map);
    } else {
      map.dragging.disable();
      map.scrollWheelZoom.disable();
      map.doubleClickZoom.disable();
      map.touchZoom.disable();
      try {
        zoomCtrl.current.remove();
      } catch (e) {}
    }
    const id = setTimeout(() => map.invalidateSize(), 350);
    return () => clearTimeout(id);
  }, [active, map]);
  return null;
}

export const LocationMap = () => {
  const { t } = useLang();
  const l = t.location;
  const [active, setActive] = useState(false);
  const [light, setLight] = useState(false);
  const [openNow, setOpenNow] = useState(isOpenNow());
  useEffect(() => {
    const id = setInterval(() => setOpenNow(isOpenNow()), 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="standort"
      className="relative border-t border-[var(--line)] bg-[var(--bg-0)] py-24 md:py-36"
      data-testid="location-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-4">
            <Reveal>
              <span className="font-mono2 text-[11px] tracking-[0.4em] uppercase text-[var(--txt-mut)]">
                {l.label}
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-heading font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl mt-6 leading-[0.95]">
                {l.headingLines[0]}
                <br />
                <span className="text-[var(--accent)]">{l.headingLines[1]}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 flex items-start gap-3 text-[var(--txt-dim)]">
                <MapPin className="h-5 w-5 mt-0.5 text-[var(--accent)]" strokeWidth={1.4} />
                <div className="font-body font-light text-base md:text-lg leading-relaxed">
                  {l.addressLines[0]}
                  <br />
                  {l.addressLines[1]}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-5 flex items-start gap-3 text-[var(--txt-dim)]" data-testid="opening-hours">
                <Clock className="h-5 w-5 mt-0.5 text-[var(--accent)]" strokeWidth={1.4} />
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[var(--txt-mut)]">
                      {l.hoursLabel}
                    </span>
                    <span
                      data-testid="open-status"
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border font-mono2 text-[9px] tracking-[0.2em] uppercase ${
                        openNow
                          ? "text-emerald-300 border-emerald-400/40"
                          : "text-[var(--txt-mut)] border-[var(--line)]"
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          openNow ? "bg-emerald-400" : "bg-[var(--txt-mut)]"
                        }`}
                      />
                      {openNow ? l.openNow : l.closedNow}
                    </span>
                  </div>
                  <div className="font-body font-light text-base md:text-lg leading-relaxed">
                    {l.hours}
                  </div>
                  <div className="font-body font-light text-sm text-[var(--txt-mut)] mt-1">
                    {l.hoursNote}
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setActive((a) => !a)}
                  data-testid="map-toggle"
                  className="inline-flex items-center gap-2 border border-[var(--line)] hover:border-[var(--accent)] px-5 py-3 font-mono2 text-[11px] tracking-[0.25em] uppercase text-[var(--txt-dim)] hover:text-[var(--txt)] transition-colors duration-300 active:scale-95"
                >
                  <Move3d className="h-4 w-4" strokeWidth={1.4} />
                  {active ? l.view3d : l.explore}
                </button>
                <button
                  onClick={openRoute}
                  data-testid="map-route"
                  className="inline-flex items-center gap-2 bg-[var(--accent)] text-black hover:bg-white px-5 py-3 font-mono2 text-[11px] tracking-[0.25em] uppercase transition-colors duration-300 active:scale-95"
                >
                  <Navigation className="h-4 w-4" strokeWidth={1.6} />
                  {l.route}
                </button>
                <button
                  onClick={() => setLight((v) => !v)}
                  data-testid="map-style-toggle"
                  className="inline-flex items-center gap-2 border border-[var(--line)] hover:border-[var(--accent)] px-5 py-3 font-mono2 text-[11px] tracking-[0.25em] uppercase text-[var(--txt-dim)] hover:text-[var(--txt)] transition-colors duration-300 active:scale-95"
                >
                  {light ? (
                    <Moon className="h-4 w-4" strokeWidth={1.4} />
                  ) : (
                    <Sun className="h-4 w-4" strokeWidth={1.4} />
                  )}
                  {light ? l.mapDark : l.mapLight}
                </button>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <div className="map-stage" data-testid="map-container">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-70px" }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              >
                <div
                  className={`map-tilt ${active ? "flat" : ""} ${light ? "map-light" : "map-dark"} h-[380px] md:h-[520px] relative`}
                >
                <MapContainer
                  center={OFFICE}
                  zoom={17}
                  scrollWheelZoom={active}
                  dragging={active}
                  doubleClickZoom={active}
                  zoomControl={active}
                  attributionControl={true}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    maxZoom={19}
                    attribution="&copy; OpenStreetMap contributors"
                  />
                  <Marker position={OFFICE} icon={pinIcon}>
                    <Tooltip
                      permanent
                      direction="top"
                      offset={[0, -10]}
                      className={`pin-tip ${openNow ? "is-open" : ""}`}
                    >
                      <span className="pin-tip-inner">
                        <span className="pin-tip-dot" />
                        {openNow ? l.openNow : l.closedNow}
                      </span>
                    </Tooltip>
                    <Popup>
                      <strong>{l.popupTitle}</strong>
                      <br />
                      {l.addressLines[0]}, {l.addressLines[1]}
                      <br />
                      <a href={GMAPS_ROUTE} target="_blank" rel="noopener noreferrer">
                        {l.route} →
                      </a>
                    </Popup>
                  </Marker>
                  <MapController active={active} />
                </MapContainer>

                {!active && (
                  <button
                    onClick={() => setActive(true)}
                    data-testid="map-overlay"
                    className="absolute inset-0 z-[500] flex items-end justify-center pb-10 bg-gradient-to-t from-black/50 via-transparent to-transparent"
                    aria-label={l.overlay}
                  >
                    <span className="font-mono2 text-[10px] tracking-[0.35em] uppercase text-[var(--txt)] bg-black/50 backdrop-blur-md px-4 py-2 border border-white/10">
                      {l.overlay}
                    </span>
                  </button>
                )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
